import React from "react";
import { Icon, EmptyState, notify, confirmDialog, useLocalPref } from "./registry";
/* ============================================================
   Fence Works — FW Admin → Status & To-do (platform admins only)

   One place to see what's switched on, what we're waiting on, and what's
   next, and to update it without asking in chat.

   Readiness      read from the service's environment by
                  server/src/platformstatus.js: per feature, what it
                  unlocks, on / off / missing / partial, the variable(s)
                  that change it and where it's documented. Names only,
                  never a value. Plus the migrations summary (the same
                  probe as Ops & Health).
   Waiting on     outside things, or a step only the owner can do.
   To-do & ideas  by section, status and priority.

   The two lists live in platform_todos (migration #35). Until it runs they
   show the starter items read-only and say "run migration #35".
   ============================================================ */

const fsApi = (): any => (window as any).VFP_DB && (window as any).VFP_DB.platformStatus;

const FS_SECTIONS = ["Lead growth", "Quotes & sales", "Crew & field", "Websites", "Platform & ops"];
const FS_WAIT_STATUS: [string, string][] = [["action", "Needs you"], ["waiting", "Waiting"], ["done", "Done"]];
const FS_TODO_STATUS: [string, string][] = [["next", "Next"], ["doing", "Doing"], ["idea", "Idea"], ["benched", "Benched"], ["done", "Done"]];
const FS_PRIORITY: [string, string][] = [["high", "High"], ["medium", "Medium"], ["low", "Low"]];
const FS_STATE: any = {
  on: { label: "On", color: "var(--green)" }, off: { label: "Off", color: "var(--fg-3)" },
  missing: { label: "Missing", color: "var(--red)" }, partial: { label: "Partial", color: "var(--amber)" },
};
const FS_STATUS_COLOR: any = {
  action: "var(--amber)", waiting: "var(--teal)", done: "var(--green)",
  next: "var(--teal)", doing: "var(--violet)", idea: "var(--fg-3)", benched: "var(--fg-faint)",
};
const FS_PRIORITY_COLOR: any = { high: "var(--red)", medium: "var(--fg-2)", low: "var(--fg-faint)" };
const FS_DOC_BASE = "https://github.com/KodiakCoolers/fenceworks/blob/main/";

/* ---------- pure helpers (web/scripts/unit-fwstatus.mjs) ---------- */
function fsStateView(state: string) {
  return FS_STATE[state] || { label: String(state || "unknown"), color: "var(--fg-3)" };
}
function fsStatusLabel(kind: string, status: string) {
  const list = kind === "waiting" ? FS_WAIT_STATUS : FS_TODO_STATUS;
  const hit = list.find((s) => s[0] === status);
  return hit ? hit[1] : String(status || "");
}
const fsPlural = (n: number, one: string, many: string) => n + " " + (n === 1 ? one : many);
/* the line at the top: what needs Rob, what we're waiting on, what's off, what's missing */
function fsSummary(data: any) {
  const waiting = ((data && data.waiting) || []).filter((w: any) => w.status !== "done");
  const needYou = waiting.filter((w: any) => w.status === "action").length;
  const waitingOn = waiting.filter((w: any) => w.status === "waiting");
  const c = (data && data.readiness && data.readiness.counts) || {};
  const featuresOff = (Number(c.off) || 0) + (Number(c.missing) || 0) + (Number(c.partial) || 0);
  const m = data && data.migrations;
  const migrationsMissing = m && m.available ? (Number(m.missing) || 0) + (Number(m.drift) || 0) : null;
  const parts: { key: string; text: string; tone: string; target: string }[] = [];
  parts.push(needYou ? { key: "need", text: needYou + (needYou === 1 ? " thing needs you" : " things need you"), tone: "amber", target: "fs-waiting" }
    : { key: "need", text: "Nothing needs you", tone: "green", target: "fs-waiting" });
  if (waitingOn.length === 1) parts.push({ key: "waiting", text: "1 waiting: " + waitingOn[0].title, tone: "teal", target: "fs-waiting" });
  else if (waitingOn.length) parts.push({ key: "waiting", text: waitingOn.length + " waiting on others", tone: "teal", target: "fs-waiting" });
  if (featuresOff) parts.push({ key: "off", text: fsPlural(featuresOff, "feature off", "features off"), tone: "muted", target: "fs-readiness" });
  if (migrationsMissing) parts.push({ key: "migrations", text: fsPlural(migrationsMissing, "migration missing", "migrations missing"), tone: "red", target: "fs-readiness" });
  else if (migrationsMissing === 0) parts.push({ key: "migrations", text: "Migrations up to date", tone: "green", target: "fs-readiness" });
  return { needYou, waitingOn: waitingOn.length, featuresOff, migrationsMissing, parts };
}
/* counts per status, plus "open" (everything not done) and "all" */
function fsCounts(items: any[], statuses: [string, string][]) {
  const out: any = { open: 0, all: 0 };
  for (const [s] of statuses) out[s] = 0;
  for (const i of items || []) { out.all++; if (i.status !== "done") out.open++; if (out[i.status] != null) out[i.status]++; }
  return out;
}
function fsFilter(items: any[], status: string, section: string) {
  return (items || []).filter((i: any) => (status === "all" ? true : status === "open" ? i.status !== "done" : i.status === status)
    && (!section || section === "all" ? true : i.section === section));
}
/* to-dos by section, in the fixed order; anything else last */
function fsGroups(items: any[]) {
  const out: [string, any[]][] = [];
  for (const s of FS_SECTIONS) { const list = (items || []).filter((i: any) => i.section === s); if (list.length) out.push([s, list]); }
  const other = (items || []).filter((i: any) => FS_SECTIONS.indexOf(i.section) < 0);
  if (other.length) out.push(["Other", other]);
  return out;
}
/* Move `id` one step up (-1) or down (+1) past its neighbour among `peers`
   (the items shown next to it), inside the full ordered list of its kind.
   → the new full order of ids, or null when it can't move that way. */
function fsMove(ordered: string[], id: string, dir: number, peers: string[]) {
  const p = (peers || []).indexOf(id);
  const other = p < 0 ? undefined : peers[p + dir];
  if (!other) return null;
  const out = (ordered || []).slice();
  const a = out.indexOf(id), b = out.indexOf(other);
  if (a < 0 || b < 0) return null;
  out[a] = other; out[b] = id;
  return out;
}
/* the edit form → what the service takes for this list */
function fsPayload(form: any, kind: string) {
  const f = form || {};
  const body: any = {
    title: String(f.title || "").replace(/\s+/g, " ").trim(),
    detail: String(f.detail || "").trim(),
    notes: String(f.notes || "").trim(),
    status: f.status || (kind === "waiting" ? "waiting" : "next"),
  };
  if (kind === "waiting") {
    body.since = f.since ? String(f.since) : null;
    body.next_check = f.next_check ? String(f.next_check) : null;
  } else {
    body.section = f.section || FS_SECTIONS[FS_SECTIONS.length - 1];
    body.priority = f.priority || "medium";
  }
  return body;
}
function fsBlank(kind: string, section?: string) {
  return kind === "waiting"
    ? { title: "", detail: "", notes: "", status: "waiting", since: new Date().toISOString().slice(0, 10), next_check: "" }
    : { title: "", detail: "", notes: "", status: "next", priority: "medium", section: section && FS_SECTIONS.indexOf(section) >= 0 ? section : "Platform & ops" };
}
/* a waiting item whose next-check date has come */
function fsDue(item: any, today: string) {
  return !!(item && item.next_check && item.status !== "done" && String(item.next_check) <= String(today));
}
function fsDate(d: any) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ""));
  if (!m) return "";
  const t = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return t.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC", year: +m[1] === new Date().getUTCFullYear() ? undefined : "numeric" });
}
function fsAgo(iso: any, now: number) {
  const t = Date.parse(String(iso || ""));
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, (now - t) / 1000);
  return s < 90 ? "just now" : s < 3600 ? Math.round(s / 60) + "m ago" : s < 86400 ? Math.round(s / 3600) + "h ago" : s < 86400 * 45 ? Math.round(s / 86400) + "d ago" : Math.round(s / (86400 * 30)) + "mo ago";
}
function fsDocUrl(path: string) {
  const p = String(path || "").replace(/^\/+/, "");
  return /^[A-Za-z0-9._/-]+$/.test(p) ? FS_DOC_BASE + p : "";
}

/* ---------- small pieces ---------- */
function FsPill({ label, color, title }: any) {
  return <span className="chip fs-pill" title={title} style={{ color, borderColor: "currentColor" }}>{label}</span>;
}
function FsSection({ id, title, sub, right, children }: any) {
  return (
    <section id={id} className="fs-section">
      <div className="fs-section-head">
        <div style={{ minWidth: 0 }}>
          <h2 className="fs-h2">{title}</h2>
          {sub && <p className="muted fs-sub">{sub}</p>}
        </div>
        {right && <div className="fs-section-right">{right}</div>}
      </div>
      {children}
    </section>
  );
}

/* ---------- readiness ---------- */
function FsReadiness({ data, go }: any) {
  const r = (data && data.readiness) || { groups: [], counts: {} };
  const m = (data && data.migrations) || { available: false };
  const [open, setOpen] = React.useState<any>({});
  const toggle = (id: string) => setOpen((o: any) => ({ ...o, [id]: !o[id] }));
  const c = r.counts || {};
  return (
    <FsSection id="fs-readiness" title="Readiness"
      sub="Read from this server's settings when the page loads: names only, never a value. Change a setting in server/.env and restart the service."
      right={<div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
        {["on", "partial", "off", "missing"].map((s) => c[s] ? <FsPill key={s} label={c[s] + " " + fsStateView(s).label.toLowerCase()} color={fsStateView(s).color} /> : null)}
      </div>}>
      <div className="card fs-mig">
        <div className="row between" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Migrations</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              {m.available
                ? [m.applied + " of " + m.total + " applied", m.missing + " missing", m.drift ? m.drift + " recorded but not there" : null, m.unknown ? m.unknown + " couldn't be checked" : null].filter(Boolean).join(" · ")
                : "Couldn't read the database's migrations right now."}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => go && go("ops")}>Ops &amp; Health<Icon name="chevright" size={13} /></button>
        </div>
        {m.available && (m.missingList || []).length > 0 && (
          <div className="fs-mig-list">
            {m.missingList.map((x: any) => (
              <div key={x.name} className="fs-mig-row">
                <code className="fs-code" style={{ color: "var(--red)" }}>{x.file}</code>
                {x.impact && <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{x.impact}</div>}
              </div>
            ))}
            <div className="faint" style={{ fontSize: 11.5 }}>Run each in the Supabase SQL editor, in the order docs/MIGRATIONS.md gives.</div>
          </div>
        )}
      </div>

      <div className="fs-groups">
        {r.groups.map((g: any) => {
          const sv = fsStateView(g.state);
          const isOpen = !!open[g.id];
          return (
            <div key={g.id} className={"card fs-group" + (isOpen ? " open" : "")}>
              <button className="fs-group-head" onClick={() => toggle(g.id)} aria-expanded={isOpen}>
                <span className="fs-dot" style={{ background: sv.color }} />
                <span className="fs-group-title">{g.title}</span>
                <span className="faint fs-group-count">{g.on} of {g.total} on</span>
                <Icon name="chevdown" size={14} style={{ transform: isOpen ? "rotate(180deg)" : undefined, transition: "transform .15s", flexShrink: 0 }} />
              </button>
              {isOpen && (
                <div className="fs-rows">
                  {g.items.map((it: any) => {
                    const iv = fsStateView(it.state);
                    const doc = fsDocUrl(it.doc || g.doc);
                    return (
                      <div key={it.id} className="fs-row">
                        <div className="row between" style={{ gap: 8, alignItems: "flex-start" }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, minWidth: 0 }}>{it.label}</div>
                          <FsPill label={iv.label} color={iv.color} />
                        </div>
                        <div className="fs-envs">{it.env.map((n: string) => <code key={n} className="fs-code">{n}</code>)}</div>
                        {it.note && <div style={{ fontSize: 12.5, color: it.state === "on" ? "var(--fg-3)" : "var(--amber)", marginTop: 4 }}>{it.note}</div>}
                        <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{it.how}
                          {doc && <> <a href={doc} target="_blank" rel="noopener" className="fs-doc" title={it.doc || g.doc}>{String(it.doc || g.doc).split("/").pop()}</a></>}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </FsSection>
  );
}

/* ---------- one item: view or edit ---------- */
function FsEditor({ kind, initial, busy, onSave, onCancel, err }: any) {
  const [f, setF] = React.useState<any>(initial);
  const set = (k: string, v: any) => setF((x: any) => ({ ...x, [k]: v }));
  const statuses = kind === "waiting" ? FS_WAIT_STATUS : FS_TODO_STATUS;
  const id = (k: string) => "fs-" + kind + "-" + k;
  return (
    <form className="fs-editor" onSubmit={(e) => { e.preventDefault(); onSave(f); }}>
      <label className="field-label" htmlFor={id("title")}>Title</label>
      <input id={id("title")} className="input" value={f.title} maxLength={200} autoFocus onChange={(e) => set("title", e.target.value)} style={{ width: "100%" }} />
      <label className="field-label" htmlFor={id("detail")}>Detail</label>
      <textarea id={id("detail")} className="textarea" rows={4} value={f.detail} onChange={(e) => set("detail", e.target.value)} style={{ width: "100%" }} placeholder="What it is, and how" />
      <div className="fs-editor-grid">
        {kind === "todo" && <div>
          <label className="field-label" htmlFor={id("section")}>Section</label>
          <select id={id("section")} className="select" value={f.section} onChange={(e) => set("section", e.target.value)} style={{ width: "100%" }}>
            {FS_SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>}
        <div>
          <label className="field-label" htmlFor={id("status")}>Status</label>
          <select id={id("status")} className="select" value={f.status} onChange={(e) => set("status", e.target.value)} style={{ width: "100%" }}>
            {statuses.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {kind === "todo" && <div>
          <label className="field-label" htmlFor={id("priority")}>Priority</label>
          <select id={id("priority")} className="select" value={f.priority} onChange={(e) => set("priority", e.target.value)} style={{ width: "100%" }}>
            {FS_PRIORITY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>}
        {kind === "waiting" && <div>
          <label className="field-label" htmlFor={id("since")}>Since</label>
          <input id={id("since")} className="input" type="date" value={f.since || ""} onChange={(e) => set("since", e.target.value)} style={{ width: "100%" }} />
        </div>}
        {kind === "waiting" && <div>
          <label className="field-label" htmlFor={id("next")}>Check again</label>
          <input id={id("next")} className="input" type="date" value={f.next_check || ""} onChange={(e) => set("next_check", e.target.value)} style={{ width: "100%" }} />
        </div>}
      </div>
      <label className="field-label" htmlFor={id("notes")}>Notes</label>
      <textarea id={id("notes")} className="textarea" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} style={{ width: "100%" }} placeholder="Where it's at, who you talked to" />
      {err && <p style={{ color: "var(--red)", fontSize: 13, margin: "8px 0 0" }}>{err}</p>}
      <div className="row gap-8" style={{ marginTop: 10, flexWrap: "wrap" }}>
        <button className="btn btn-primary btn-sm" type="submit" disabled={!!busy || !String(f.title || "").trim()}>{busy ? "Saving…" : "Save"}</button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel} disabled={!!busy}>Cancel</button>
      </div>
    </form>
  );
}

function FsItem({ item, kind, editable, busy, canUp, canDown, onMove, onEdit, onStatus, onDelete, today, now }: any) {
  const due = fsDue(item, today);
  const done = item.status === "done";
  return (
    <div className={"fs-item" + (done ? " done" : "")}>
      <div className="fs-item-main">
        <div className="fs-item-title">
          <FsPill label={fsStatusLabel(kind, item.status)} color={FS_STATUS_COLOR[item.status] || "var(--fg-3)"} />
          {kind === "todo" && item.priority && <FsPill label={FS_PRIORITY.find((p) => p[0] === item.priority)?.[1] || item.priority} color={FS_PRIORITY_COLOR[item.priority]} title="Priority" />}
          <b className="fs-title-text">{item.title}</b>
        </div>
        {item.detail && <div className="fs-detail">{item.detail}</div>}
        {item.notes && <div className="fs-notes"><span className="faint">Notes:</span> {item.notes}</div>}
        <div className="faint fs-meta">
          {[
            kind === "waiting" && item.since ? "since " + fsDate(item.since) : null,
            kind === "waiting" && item.next_check ? (due ? "check due " : "check ") + fsDate(item.next_check) : null,
            item.seed ? "starter item" : item.updated_by ? "updated by " + item.updated_by + (item.updated_at ? " · " + fsAgo(item.updated_at, now) : "") : null,
          ].filter(Boolean).map((t: any, i: number) => <span key={i} style={due && /^check due/.test(t) ? { color: "var(--red)", fontWeight: 600 } : undefined}>{i ? " · " : ""}{t}</span>)}
        </div>
      </div>
      {editable && (
        <div className="fs-actions">
          <button className="btn btn-ghost btn-sm fs-icon" title="Move up" aria-label="Move up" disabled={!!busy || !canUp} onClick={() => onMove(-1)}><Icon name="chevdown" size={14} style={{ transform: "rotate(180deg)" }} /></button>
          <button className="btn btn-ghost btn-sm fs-icon" title="Move down" aria-label="Move down" disabled={!!busy || !canDown} onClick={() => onMove(1)}><Icon name="chevdown" size={14} /></button>
          <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={onEdit}><Icon name="edit" size={13} />Edit</button>
          {done
            ? <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => onStatus(kind === "waiting" ? "action" : "next")}><Icon name="undo" size={13} />Reopen</button>
            : <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={() => onStatus("done")}><Icon name="check" size={13} />Done</button>}
          <button className="btn btn-ghost btn-sm fs-icon" title="Delete" aria-label="Delete" disabled={!!busy} onClick={onDelete} style={{ color: "var(--red)" }}><Icon name="trash" size={13} /></button>
        </div>
      )}
    </div>
  );
}

/* ---------- the page ---------- */
function PlatformStatusPage({ go }: any) {
  const api = fsApi();
  const live = !!(api && api.enabled && api.enabled());
  const [data, setData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState<any>(true);
  const [refreshing, setRefreshing] = React.useState<any>(false);
  const [denied, setDenied] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(null);
  const [editing, setEditing] = React.useState<any>(null);   // item id, or "new:waiting" / "new:todo"
  const [formErr, setFormErr] = React.useState<any>("");
  const [status, setStatus] = useLocalPref("fw_fsstatus_status", "open");
  const [section, setSection] = useLocalPref("fw_fsstatus_section", "all");
  const [showDone, setShowDone] = React.useState<any>(false);

  const load = React.useCallback((fresh?: boolean) => {
    if (!live) { setLoading(false); return Promise.resolve(); }
    setErr(""); if (fresh) setRefreshing(true);
    return api.load(!!fresh).then((d: any) => {
      setData(d); setLoading(false); setDenied(false);
      if (d && d.seeded) notify("Added the " + d.seeded + " starter items. Edit them to match where things are.");
    }).catch((e: any) => { setLoading(false); if (e && e.status === 403) setDenied(true); else setErr(String((e && e.message) || e)); })
      .finally(() => setRefreshing(false));
  }, [live]);
  React.useEffect(() => { load(); }, [load]);

  const setup = !!(data && data.setup);
  const waiting: any[] = (data && data.waiting) || [];
  const todos: any[] = (data && data.todos) || [];
  const today = new Date().toISOString().slice(0, 10);
  const now = Date.now();
  const summary = fsSummary(data);

  const patchItem = (item: any) => setData((d: any) => {
    const key = item.kind === "waiting" ? "waiting" : "todos";
    const list = d[key].some((x: any) => x.id === item.id) ? d[key].map((x: any) => (x.id === item.id ? item : x)) : [...d[key], item];
    return { ...d, [key]: list };
  });
  const failMsg = (e: any) => String((e && e.message) || e);

  async function save(kind: string, id: string | null, form: any) {
    setBusy("save"); setFormErr("");
    try {
      const body = fsPayload(form, kind);
      const out = id ? await api.update(id, body) : await api.create({ kind, ...body });
      patchItem(out.item); setEditing(null);
      notify(id ? "Saved." : "Added.");
    } catch (e: any) { setFormErr(failMsg(e)); }
    finally { setBusy(null); }
  }
  async function quickStatus(item: any, next: string) {
    setBusy("st:" + item.id); setErr("");
    try { const out = await api.update(item.id, { status: next }); patchItem(out.item); }
    catch (e: any) { setErr(failMsg(e)); }
    finally { setBusy(null); }
  }
  async function remove(item: any) {
    const ok = await confirmDialog({
      title: "Delete “" + item.title + "”?",
      body: "It's gone from the list for good." + (item.starter ? " A starter item can come back with Add missing starter items." : ""),
      confirmLabel: "Delete", danger: true,
    });
    if (!ok) return;
    setBusy("rm:" + item.id); setErr("");
    try {
      await api.remove(item.id);
      // a deleted starter item is one "Add missing starter items" can put back
      setData((d: any) => ({ ...d, waiting: d.waiting.filter((x: any) => x.id !== item.id), todos: d.todos.filter((x: any) => x.id !== item.id), missingSeeds: (Number(d.missingSeeds) || 0) + (item.starter ? 1 : 0) }));
      notify("Deleted.");
    } catch (e: any) { setErr(failMsg(e)); }
    finally { setBusy(null); }
  }
  async function move(kind: string, item: any, dir: number, peers: string[]) {
    const key = kind === "waiting" ? "waiting" : "todos";
    const ordered = data[key].map((x: any) => x.id);
    const next = fsMove(ordered, item.id, dir, peers);
    if (!next) return;
    const prev = data;
    // move it on screen at once; put it back if the service says no
    setData((d: any) => ({ ...d, [key]: next.map((id: string, i: number) => ({ ...d[key].find((x: any) => x.id === id), position: i })) }));
    setBusy("mv:" + item.id);
    try { await api.reorder(kind, next); }
    catch (e: any) { setData(prev); setErr(failMsg(e)); }
    finally { setBusy(null); }
  }
  async function seed() {
    setBusy("seed"); setErr("");
    try {
      const out = await api.seed();
      notify(out.added ? "Added " + out.added + " starter item" + (out.added === 1 ? "" : "s") + "." : "Every starter item is already here.");
      await load();
    } catch (e: any) { setErr(failMsg(e)); }
    finally { setBusy(null); }
  }
  const scrollTo = (id: string) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  if (!live) return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="clipboard" title="Needs the live service" sub="Status & To-do lives on the automation service (app.fenceworks.ai)." /></div>
    </div>
  );
  if (denied) return (
    <div className="page fade-up" style={{ maxWidth: 640 }}>
      <div className="card pad" style={{ textAlign: "center" }}><EmptyState icon="lock" title="Owner only" sub="Limited to Fence Works platform admins (FW Admin → Admins)." />
        <button className="btn btn-primary" onClick={() => go && go("dashboard")}>Back to dashboard</button></div>
    </div>
  );

  const renderList = (kind: string, items: any[], peersOf: (i: any) => string[]) => items.map((item: any) => {
    const peers = peersOf(item);
    const p = peers.indexOf(item.id);
    if (item.id && editing === item.id) return (
      <div key={item.id} className="fs-item editing">
        <FsEditor kind={kind} busy={busy === "save"} err={formErr}
          initial={{ title: item.title, detail: item.detail || "", notes: item.notes || "", status: item.status, priority: item.priority || "medium", section: item.section || "Platform & ops", since: item.since || "", next_check: item.next_check || "" }}
          onSave={(f: any) => save(kind, item.id, f)} onCancel={() => { setEditing(null); setFormErr(""); }} />
      </div>
    );
    return <FsItem key={item.id || item.key} item={item} kind={kind} editable={setup && !!item.id} busy={busy} today={today} now={now}
      canUp={p > 0} canDown={p >= 0 && p < peers.length - 1}
      onMove={(dir: number) => move(kind, item, dir, peers)} onEdit={() => { setEditing(item.id); setFormErr(""); }}
      onStatus={(s: string) => quickStatus(item, s)} onDelete={() => remove(item)} />;
  });
  const adder = (kind: string) => editing === "new:" + kind && (
    <div className="fs-item editing">
      <FsEditor kind={kind} busy={busy === "save"} err={formErr} initial={fsBlank(kind, section)}
        onSave={(f: any) => save(kind, null, f)} onCancel={() => { setEditing(null); setFormErr(""); }} />
    </div>
  );
  const addBtn = (kind: string, label: string) => setup && (
    <button className="btn btn-primary btn-sm" disabled={!!busy || editing === "new:" + kind} onClick={() => { setEditing("new:" + kind); setFormErr(""); }}><Icon name="plus" size={14} />{label}</button>
  );

  // Waiting on: open ones in order, then the done ones tucked away
  const wOpen = waiting.filter((w) => w.status !== "done");
  const wDone = waiting.filter((w) => w.status === "done");
  const wCounts = fsCounts(waiting, FS_WAIT_STATUS);
  // To-do: filtered, grouped by section when showing them all
  const tCounts = fsCounts(fsFilter(todos, "all", section), FS_TODO_STATUS);
  const shown = fsFilter(todos, status, section);
  const groups = section === "all" ? fsGroups(shown) : [[section, shown] as [string, any[]]];

  return (
    <div className="page fade-up fs-page" style={{ maxWidth: 980 }}>
      <div className="row between" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Status &amp; To-do</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>What's switched on, what we're waiting on, and what's next. Readiness comes from the server; the two lists are yours to edit.</p>
        </div>
        <button className="btn btn-ghost btn-sm" disabled={loading || refreshing} onClick={() => load(true)} title="Check the settings, Stripe and the migrations again">{refreshing ? "Checking…" : "Refresh"}</button>
      </div>

      {loading ? <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: 24 }}>Loading…</p> : !data ? (
        err ? <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p> : null
      ) : <>
        <div className="fs-summary card">
          {summary.parts.map((p, i) => (
            <React.Fragment key={p.key}>
              {i > 0 && <span className="fs-sep" aria-hidden="true">·</span>}
              <button className={"fs-sum fs-" + p.tone} onClick={() => scrollTo(p.target)}>{p.text}</button>
            </React.Fragment>
          ))}
        </div>
        {err && <p style={{ color: "var(--red)", fontSize: 13 }}>{err}</p>}
        {!setup && (
          <div className="card fs-setup">
            <b>Run migration #{(data.migration && data.migration.number) || 35} to edit these lists.</b>{" "}
            <span className="muted">Paste <code className="fs-code">{(data.migration && data.migration.file) || "supabase/migrate_2026-10-13_platform_status.sql"}</code> into the Supabase SQL editor and run it. Until then the starter items below are read-only; readiness works either way.</span>
          </div>
        )}

        <FsReadiness data={data} go={go} />

        <FsSection id="fs-waiting" title="Waiting on"
          sub="Outside things, or a step only you can do."
          right={<div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <span className="faint" style={{ fontSize: 12 }}>{wCounts.action} need you · {wCounts.waiting} waiting</span>
            {addBtn("waiting", "Add")}
          </div>}>
          <div className="card fs-list">
            {adder("waiting")}
            {wOpen.length === 0 && editing !== "new:waiting"
              ? <EmptyState icon="check" title="Nothing waiting" sub="Add something when you're waiting on Google, a supplier, or yourself." />
              : renderList("waiting", wOpen, () => wOpen.map((x) => x.id))}
            {wDone.length > 0 && (
              <div className="fs-done-toggle">
                <button className="btn btn-ghost btn-sm" onClick={() => setShowDone(!showDone)}><Icon name="chevdown" size={13} style={{ transform: showDone ? "rotate(180deg)" : undefined }} />{showDone ? "Hide" : "Show"} done ({wDone.length})</button>
              </div>
            )}
            {showDone && renderList("waiting", wDone, () => wDone.map((x) => x.id))}
          </div>
        </FsSection>

        <FsSection id="fs-todo" title="To-do & ideas"
          sub="What's next, what's being built, and what's parked."
          right={addBtn("todo", "Add")}>
          <div className="fs-filters">
            <div className="cat-tabs" style={{ margin: 0 }}>
              {[["open", "Open"], ...FS_TODO_STATUS, ["all", "All"]].map(([id, label]) => (
                <button key={id} className={"filter-chip" + (status === id ? " on" : "")} onClick={() => setStatus(id)}>{label} <span className="faint tnum">{tCounts[id] || 0}</span></button>
              ))}
            </div>
            <select className="select fs-section-select" value={section} onChange={(e) => setSection(e.target.value)} aria-label="Section">
              <option value="all">All sections</option>
              {FS_SECTIONS.map((s) => <option key={s} value={s}>{s} ({todos.filter((t) => t.section === s && t.status !== "done").length})</option>)}
            </select>
          </div>
          {adder("todo") && <div className="card fs-list" style={{ marginBottom: 10 }}>{adder("todo")}</div>}
          {shown.length === 0
            ? <div className="card"><EmptyState icon="check" title="Nothing here" sub={status === "open" ? "No open to-dos in this view." : "No to-dos match these filters."} /></div>
            : groups.map(([name, items]) => (
              <div key={name} className="fs-todo-group">
                {section === "all" && <div className="kicker fs-group-kicker">{name} <span className="faint">({items.length})</span></div>}
                <div className="card fs-list">
                  {renderList("todo", items, (i: any) => items.filter((x: any) => x.section === i.section).map((x: any) => x.id))}
                </div>
              </div>
            ))}
          {setup && (
            <div className="row gap-8 fs-seed" style={{ alignItems: "center", flexWrap: "wrap" }}>
              <button className="btn btn-ghost btn-sm" disabled={!!busy || !data.missingSeeds} onClick={seed}><Icon name="download" size={13} />{busy === "seed" ? "Adding…" : "Add missing starter items"}</button>
              <span className="faint" style={{ fontSize: 11.5 }}>{data.missingSeeds ? data.missingSeeds + " starter item" + (data.missingSeeds === 1 ? " isn't" : "s aren't") + " on your lists. Adding them never changes an item you edited." : "Every starter item is on your lists."}</span>
            </div>
          )}
        </FsSection>
      </>}
    </div>
  );
}

Object.assign(window, { PlatformStatusPage });

export { PlatformStatusPage };
